import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import SimpleModal from './common/SimpleModal';
import ReportModal from './documents/ReportModal/ReportModal';
import './CompanyReportsModal.css';

const getBackendUrl = () => {
  if (process.env.REACT_APP_BACKEND_URL) return process.env.REACT_APP_BACKEND_URL;
  if (process.env.BACKEND_URL) return process.env.BACKEND_URL;
  if (typeof window !== 'undefined' && window.location.hostname) {
    const hostname = window.location.hostname;
    if (
      hostname.includes('firebaseapp.com') ||
      hostname.includes('web.app') ||
      hostname.includes('viraltrade.in') ||
      hostname.includes('viraltrade-19032')
    ) {
      return 'https://viral-trade-backend-1081469852720.asia-south1.run.app';
    }
    if (hostname.includes('run.app')) {
      return 'https://viral-trade-backend-1081469852720.asia-south1.run.app';
    }
  }
  return 'http://localhost:8000';
};

const BASE_URL = getBackendUrl();

const CompanyReportsModal = ({ isOpen, onClose, company }) => {
  const [reportType, setReportType] = useState('all');
  const [year, setYear] = useState('');
  const [availableYears, setAvailableYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);

  const title = useMemo(() => {
    if (!company) return '';
    return `${company.symbol} — ${company.name}`;
  }, [company]);

  useEffect(() => {
    if (!isOpen) return;
    // load filter options
    (async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/company-reports/available-filters`);
        const years = res?.data?.filters?.years || [];
        setAvailableYears(years);
      } catch (e) {
        // ignore silently for now
      }
    })();
  }, [isOpen]);

  const buildStaticReports = () => {
    // Provide 5 static placeholder PDFs (PDF 1..5)
    return Array.from({ length: 5 }).map((_, i) => ({
      id: `static-${i + 1}`,
      source_pdf: `PDF ${i + 1}`,
      pdf_url: '/1.pdf',
    }));
  };

  useEffect(() => {
    // reset when company changes and show 5 static values by default
    if (isOpen) {
      setReportType('all');
      setYear('');
      setReports(buildStaticReports());
      setError('');
    }
  }, [company, isOpen]);

  const search = async () => {
    if (!company?.symbol) return;
    setLoading(true);
    setError('');
    setReports([]);
    try {
      const payload = {
        company_symbol: company.symbol,
        report_type: reportType !== 'all' ? reportType : undefined,
        year: year || undefined,
        max_results: 10,
      };
      const res = await axios.post(`${BASE_URL}/api/company-reports/search`, payload);
      if (res.data?.success) {
        setReports(res.data.results || []);
      } else {
        setError('Search failed.');
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Search failed.');
    } finally {
      setLoading(false);
    }
  };

  const openReport = (report) => setSelectedReport(report);
  const closeReport = () => setSelectedReport(null);

  return (
    <>
      <SimpleModal isOpen={isOpen} onClose={onClose} title={title}>
        <div className="cr-modal-toolbar">
          <select value={reportType} onChange={(e) => setReportType(e.target.value)}>
            <option value="all">Report Type</option>
            <option value="annual">Annual</option>
            <option value="quarterly">Quarterly</option>
            <option value="financial">Financial</option>
            <option value="earnings">Earnings</option>
          </select>
          <select value={year} onChange={(e) => setYear(e.target.value)}>
            <option value="">Fiscal Year</option>
            {availableYears.map((y) => (
              <option key={y.value} value={y.value}>
                {y.label}
              </option>
            ))}
          </select>
          <button className="cr-modal-search-btn" onClick={search} disabled={loading}>
            {loading ? 'Searching…' : 'Search'}
          </button>
        </div>

        <div className="cr-modal-section-title">
          <span className="clock">🕒</span>
          <span>Recent Reports</span>
        </div>

        {error && <div className="cr-modal-error">{error}</div>}

        <ul className="cr-modal-list">
          {reports.length === 0 && !loading && (
            <li className="cr-modal-empty">No reports yet. Use filters and Search.</li>
          )}
          {reports.map((r, idx) => (
            <li key={r.id || idx} className="cr-modal-row">
              <span className="file">📄</span>
              <span className="title">{r.source_pdf || r.company_name || `PDF ${idx + 1}`}</span>
              <span className="actions">
                <button onClick={() => openReport(r)} className="link">View Summary</button>
                <span className="sep">|</span>
                {r.pdf_url ? (
                  <a href={r.pdf_url} target="_blank" rel="noreferrer" className="link">View PDF</a>
                ) : r.source_pdf ? (
                  <a href={`/uploads/${r.source_pdf}`} target="_blank" rel="noreferrer" className="link">View PDF</a>
                ) : (
                  <span className="muted">View PDF</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      </SimpleModal>

      <ReportModal isOpen={!!selectedReport} onClose={closeReport} data={selectedReport} />
    </>
  );
};

export default CompanyReportsModal;
